import { Component, effect, inject, signal, untracked } from '@angular/core';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  EWidget,
  FilterCardService,
  IFilterValuesVisibleToWidget,
  TWidgetCustomParameters,
  widgetConstants,
} from 'scw-digital-boards-widgets';
import {
  EAdvancedFilterInputMode,
  TAdvancedFilterInputsAutoSubmit,
  TModalConfiguration,
} from '../../../shared/components/advanced-filter/advanced-filter-inputs/advanced-filter-inputs.interface';
import { IFilterIdentity } from '../../../shared/components/advanced-filter/advanced-filter.interface';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import { IWidgetFilterSuppression } from '../dashboard-content.interface';
import { ConfigureFiltersOfAModePanelComponent } from '../widget-add-edit-dialog/input-components/configure-filters-of-a-mode-panel/configure-filters-of-a-mode-panel.component';
import { IConfigurationsOfAdvancedFilterInputs } from '../widget-add-edit-dialog/input-components/configure-filters-of-a-mode-panel/configure-filters-of-a-mode-panel.interface';
import { FilterModeSelectionAndOrderingPanelComponent } from '../widget-add-edit-dialog/input-components/filter-mode-selection-and-ordering-panel/filter-mode-selection-and-ordering-panel.component';
import { IFilterPanel } from '../widget-add-edit-dialog/widget-add-edit-dialog.interface';

export interface IWidgetFilterDialogData<Widget extends EWidget = EWidget> {
  filterValuesVisibleToWidget: IFilterValuesVisibleToWidget[];
  lastAppliedFilterSuppression: IWidgetFilterSuppression[];
  widgetType: Widget;
  widgetTypeBasedConfiguration: TWidgetCustomParameters[Widget];
}

export interface IWidgetFilterDialogOutput {
  filters: IFilterValuesVisibleToWidget[];
  filterSuppression: IWidgetFilterSuppression[];
}

@Component({
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    TranslatePipe,
    MatDialogContent,
    MatDialogTitle,
    ConfigureFiltersOfAModePanelComponent,
    FilterModeSelectionAndOrderingPanelComponent,
  ],
  selector: 'app-widget-filters-dialog',
  styleUrl: './widget-filters-dialog.component.scss',
  templateUrl: './widget-filters-dialog.component.html',
})
export class WidgetFiltersDialogComponent<Widget extends EWidget = EWidget> {
  public readonly injectData = inject<IWidgetFilterDialogData<Widget>>(MAT_DIALOG_DATA);
  public readonly modalRef: MatDialogRef<WidgetFiltersDialogComponent> = inject(MatDialogRef);
  public readonly matDialog = inject(MatDialog);
  public readonly snackbar = inject(SnackbarService);
  public readonly translate = inject(TranslateService);
  public readonly filterCardService = inject(FilterCardService);

  public readonly filterSuppression = signal<IWidgetFilterSuppression[]>(this.injectData.lastAppliedFilterSuppression);
  public rows: IFilterPanel[] = this.injectData.filterValuesVisibleToWidget.map(({ modeName, filters }) => {
    const matchingModeFromConfiguration = widgetConstants[this.injectData.widgetType]
      .filterModes({
        typeBasedConfiguration: this.injectData.widgetTypeBasedConfiguration,
      })
      .find(({ name }) => name === modeName);
    const matchingFilterSuppression =
      this.filterSuppression().find((suppression) => suppression.modeName === modeName)?.suppressedFilters ?? [];

    return {
      filters:
        matchingModeFromConfiguration?.filters.map(({ identity }): IConfigurationsOfAdvancedFilterInputs => {
          const matchingInitialFilter = filters.find(
            (filter) =>
              filter.field === identity.field &&
              (identity.operators.includes(filter.operator) ||
                this.filterCardService.checkIfEqIsDemandedAndProvidedForSingleDropdown(identity.operators, filter) ||
                this.filterCardService.checkIfEqIsDemandedAndProvidedForDate(identity.operators, filter)),
          );

          const inputsConfiguration: TModalConfiguration = matchingInitialFilter
            ? {
                fixedField: identity.field,
                initialValues: matchingInitialFilter,
                isEqAllowed: true,
                mode: EAdvancedFilterInputMode.autoSubmit,
                type: 'edit',
              }
            : {
                fixedField: identity.field,
                isEqAllowed: true,
                mode: EAdvancedFilterInputMode.autoSubmit,
                type: 'add',
              };

          return {
            inputsConfiguration,
            selectedFilterIdentity: signal<IFilterIdentity | null>(null),
            submitData: signal<TAdvancedFilterInputsAutoSubmit>({
              data: null,
              isValid: false,
            }),
          };
        }) ?? [],
      matchingFilterSuppression,
      modeName,
    };
  });

  public readonly filterModeOrder = signal<string[]>(
    this.injectData.filterValuesVisibleToWidget.map(({ modeName }) => modeName),
  );
  public readonly filtersOfWidget = widgetConstants[this.injectData.widgetType].filterModes({
    typeBasedConfiguration: this.injectData.widgetTypeBasedConfiguration,
  });
  public readonly modeNamesOfWidget = this.filtersOfWidget.map(({ name }) => name);

  constructor() {
    effect(() => {
      const filterModeOrder = this.filterModeOrder();
      this.filterSuppression.update((currentValue) => {
        return filterModeOrder.map((modeName) => ({
          modeName,
          suppressedFilters: currentValue.find((value) => value.modeName === modeName)?.suppressedFilters ?? [],
        }));
      });

      this.rows = filterModeOrder.reduce((reducePayload, modeName) => {
        const matchingSection = this.rows.find((section) => section.modeName === modeName);

        return [
          ...reducePayload,
          matchingSection ?? {
            filters: this.filtersOfWidget
              .find(({ name }) => name === modeName)!
              .filters.map(
                ({ identity }): IConfigurationsOfAdvancedFilterInputs => ({
                  inputsConfiguration: {
                    fixedField: identity.field,
                    isEqAllowed: true,
                    mode: EAdvancedFilterInputMode.autoSubmit,
                    type: 'add',
                  },
                  selectedFilterIdentity: signal<IFilterIdentity | null>(null),
                  submitData: signal<TAdvancedFilterInputsAutoSubmit | null>(null),
                }),
              ),
            matchingFilterSuppression: untracked(this.filterSuppression).find(
              (suppression) => suppression.modeName === modeName,
            )!.suppressedFilters,
            modeName,
          },
        ];
      }, [] as IFilterPanel[]);
    });
  }

  public apply(): void {
    if (!this.filterModeOrder().length) {
      this.snackbar.open(this.translate.instant('system.message.noFilterModeSelected'), ESnackbar.error);

      return;
    }

    this.modalRef.close({
      filters: this.rows.map(({ filters, modeName }) => ({
        filters: filters
          .filter(({ submitData }) => submitData()?.data)
          .map(({ submitData }) => ({ ...submitData()!.data!, strict: true })),
        modeName,
      })),
      filterSuppression: this.filterSuppression(),
    } satisfies IWidgetFilterDialogOutput);
  }
}
